import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Project } from "../api/types";
import { api } from "../api/client";
import { Icon } from "./icons";
import { ConfirmDialog } from "./ConfirmDialog";
import { toast } from "./toast";
import { backdropClose } from "../lib/backdrop";

// One row in the mega menu: a bold label + a one-line "what's it for". Disabled
// (or "soon") items stay visible but greyed, so the menu's shape never changes —
// you always know where to look. Module-level so it isn't a fresh type each render.
function MenuItem({ label, desc, onClick, disabled, soon, danger }: {
  label: string;
  desc: string;
  onClick?: () => void;
  disabled?: boolean;
  soon?: boolean;
  danger?: boolean;
}) {
  const off = disabled || soon;
  return (
    <button
      type="button"
      role="menuitem"
      className={`mm-item${off ? " is-disabled" : ""}${danger ? " mm-danger" : ""}`}
      onClick={off ? undefined : onClick}
      disabled={off}
    >
      <span className="mm-l">{label}{soon && <span className="mm-soon">soon</span>}</span>
      <span className="mm-d">{desc}</span>
    </button>
  );
}

/**
 * ProjectMenu — the project "burger" shown on every project page AND the chapter
 * report. A mega menu organised by module (Proofing · Spec check · Manuscript ·
 * Project), so the promise holds: if something isn't a button on the page, it's
 * in here. Items grey out when they don't apply yet; everything is navigation or
 * a self-contained action, so the same panel works identically wherever it opens.
 */
export function ProjectMenu({ project }: { project: Project }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 56, right: 16 });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = project.id;
  const proofed = project.proofed > 0;

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: Math.max(12, window.innerWidth - r.right) });
    }
    setOpen((v) => !v);
  }
  const go = (to: string) => { setOpen(false); navigate(to); };
  const openTab = (to: string) => { setOpen(false); window.open(to, "_blank", "noopener"); };

  const archiveMut = useMutation({
    mutationFn: () => api(`/api/projects/${id}/archive`, { method: "POST", body: JSON.stringify({ archived: true }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast("Project archived.", "info"); navigate("/"); },
    onError: (e: any) => toast(e?.message || "Couldn't archive — try again?", "error"),
  });
  const deleteMut = useMutation({
    mutationFn: () => api(`/api/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast("Project deleted.", "info"); navigate("/"); },
    onError: (e: any) => { toast(e?.message || "Couldn't delete — try again?", "error"); setConfirmDelete(false); },
  });
  const renameMut = useMutation({
    mutationFn: (name: string) =>
      api(`/api/projects/${id}/rename`, { method: "POST", body: JSON.stringify({ name }) }),
    onSuccess: () => {
      // Refresh the detail (header/spec page) AND the lists/sidebar so the new
      // name shows everywhere immediately.
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast("Project renamed.", "success");
      setRenaming(false);
    },
    onError: (e: any) => toast(e?.message || "Couldn't rename — try again?", "error"),
  });
  function submitRename() {
    const name = newName.trim();
    if (!name || name === project.name) return;
    renameMut.mutate(name);
  }

  return (
    <div className="proj-menu">
      <button
        ref={btnRef}
        className="icon-btn"
        onClick={toggle}
        aria-label="Project menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="menu" size={18} />
      </button>

      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="mega-menu" role="menu" style={{ top: pos.top, right: pos.right }}>
            <div className="mm-cols">
              <div className="mm-col">
                <div className="mm-head"><Icon name="headphones" size={15} /> Proofing</div>
                <MenuItem label="Open report" desc="Review the flagged differences"
                  onClick={() => go(`/projects/${id}/report`)} disabled={!proofed} />
                <MenuItem label="Review manuscript" desc="How Mockingbird read your PDF"
                  onClick={() => go(`/projects/${id}/manuscript`)} disabled={!project.has_manuscript} />
                <MenuItem label="Pickups Package" desc="Picked flags to share or export"
                  onClick={() => openTab(`/projects/${id}/pickups`)} disabled={!proofed} />
              </div>

              <div className="mm-col">
                <div className="mm-head"><Icon name="audio-waveform" size={15} /> Spec check</div>
                <MenuItem label="Run technical analysis" desc="Measure loudness, peaks, noise…"
                  onClick={() => go(`/projects/${id}/specs`)} disabled={project.n_audio === 0} />
                <MenuItem label="Delivery standards" desc="Presets, or build your own" soon />
              </div>

              <div className="mm-col">
                <div className="mm-head"><Icon name="document" size={15} /> Manuscript <span className="mm-soon">soon</span></div>
                <div className="mm-note">Pronunciations, character notes and prep — coming soon.</div>
              </div>

              <div className="mm-col mm-col-div">
                <div className="mm-head"><Icon name="folder" size={15} /> Project</div>
                <MenuItem label="Audio files" desc="Add, reorder, remove recordings"
                  onClick={() => go(`/projects/${id}`)} />
                <MenuItem label="Chapter mapping" desc="Pages each file covers"
                  onClick={() => go(`/projects/${id}?adjust=mapping`)} disabled={!project.has_mapping} />
                <MenuItem label="Rename" desc="Change the project name"
                  onClick={() => { setOpen(false); setNewName(project.name); setRenaming(true); }} />
                <MenuItem label="Archive" desc="Hide, but keep it"
                  onClick={() => archiveMut.mutate()} />
                <MenuItem label="Delete" desc="Remove it for good" danger
                  onClick={() => { setOpen(false); setConfirmDelete(true); }} />
              </div>
            </div>

            <div className="mm-foot">
              <button type="button" className="mm-item mm-foot-link" onClick={() => go("/")}>
                <span className="mm-l"><Icon name="back" size={14} /> All projects</span>
              </button>
            </div>
          </div>
        </>
      )}

      {renaming && (
        <div className="modal-overlay" {...backdropClose(() => { if (!renameMut.isPending) setRenaming(false); })}>
          <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Rename project</h3>
            <div className="modal-body">
              <input
                className="input"
                autoFocus
                maxLength={120}
                value={newName}
                placeholder="Project name"
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); submitRename(); }
                  else if (e.key === "Escape") { e.preventDefault(); setRenaming(false); }
                }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setRenaming(false)} disabled={renameMut.isPending}>Cancel</button>
              <button className="btn primary" onClick={submitRename}
                disabled={renameMut.isPending || !newName.trim() || newName.trim() === project.name}>
                {renameMut.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this project?"
        body={<>This permanently removes <b>{project.name}</b> and all its files. This can’t be undone.</>}
        confirmLabel="Delete"
        danger
        busy={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
